import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type IconBadgeTone = 'indigo' | 'neutral' | 'rose' | 'amber' | 'emerald'
export type IconBadgeTheme = 'filled' | 'outline'
export type IconBadgeSize = 'ring20' | 'xs' | 'sm' | 'md' | 'lg'

export interface IconBadgeProps {
  /** The icon element, e.g. `<AlertCircle />`. Sized by `size` via a nested-svg selector. */
  icon: ReactNode
  tone?: IconBadgeTone
  theme?: IconBadgeTheme
  size?: IconBadgeSize
  className?: string
}

// Container / glyph — exactly container/2 at every size (untitledui-reference.md
// "Featured icon" master 1102:5338, Type=Light column). `sm`/`md`/`lg` land on
// our 4px scale already, so no bracket values are needed.
//
// `xs` (24/12) is NOT in the master — the master's ladder starts at `sm`
// (32/16). Onboarding's mock draws several small circular icon containers at
// 24px, below that floor — the Step 1 hero check-item pair
// (step-domain.tsx) and the locked-teaser-card icon both hand-rolled a
// bespoke 24px circle for lack of a small enough rung; they now use `xs`.
// `xs` closes that gap deliberately, keeping the master's container/2 ratio
// (24/2 = 12, still on-scale) rather than adding a fifth off-master size per
// call site.
const CONTAINER_SIZE: Record<IconBadgeSize, string> = {
  // `ring20` breaks the container/2 ratio ON PURPOSE — 20px container, 20px
  // glyph — because `theme="outline"`'s master is a different shape from the
  // filled one. `Featured icon outline` (`1130:82786`) is a bare 20px glyph
  // box with two rings around it, NOT a padded circle containing a smaller
  // glyph. The rings below are absolute insets (-4px/-9px) that reproduce the
  // master's percentage insets (-20%/-45%) at exactly a 20px container and
  // nowhere else, so before this rung existed the outline theme could not
  // render the master's proportions at any of its four sizes: `md` gave 48/58px
  // rings where the node has 28/38.
  //
  // Named for what it is rather than extending the t-shirt ladder downward:
  // `xxs` would imply the 2:1 filled geometry one step below `xs`, which this
  // is not. Filled callers should not reach for it.
  ring20: 'size-5', // 20px — outline only, see above
  xs: 'size-6', // 24px — deliberate divergence below the master's 32px floor, see above
  sm: 'size-8', // 32px
  md: 'size-10', // 40px
  lg: 'size-12', // 48px
}

const GLYPH_SIZE: Record<IconBadgeSize, string> = {
  ring20: '[&_svg]:size-5', // 20px — 1:1 with its container, see CONTAINER_SIZE
  xs: '[&_svg]:size-3', // 12px
  sm: '[&_svg]:size-4', // 16px
  md: '[&_svg]:size-5', // 20px
  lg: '[&_svg]:size-6', // 24px
}

// theme="filled" fill — same tone vocabulary as TONE_VARIANT/Tag/Badge's accent
// variants (indigo/emerald/rose/amber), not the kit's Brand/Gray/Error/Warning/
// Success literals. `neutral` uses `--surface-muted` specifically (not `--muted`,
// which Tag's neutral uses) — untitledui-reference.md's token table names
// `--surface-muted` as "icon-badge fill" for the kit's Gray `bg-tertiary`.
const FILLED_CLASS: Record<IconBadgeTone, string> = {
  indigo: 'bg-info-soft text-info',
  neutral: 'bg-surface-muted text-muted-foreground',
  rose: 'bg-rose-bg text-rose',
  amber: 'bg-amber-bg text-amber',
  emerald: 'bg-emerald-bg text-emerald',
}

const OUTLINE_TEXT_CLASS: Record<IconBadgeTone, string> = {
  indigo: 'text-info',
  neutral: 'text-muted-foreground',
  rose: 'text-rose',
  amber: 'text-amber',
  emerald: 'text-emerald',
}

// theme="outline" ring color, referenced as a token (DES-09) via CSS var — never
// a raw hex/oklch literal.
const OUTLINE_RING_VAR: Record<IconBadgeTone, string> = {
  indigo: 'var(--color-info)',
  neutral: 'var(--color-muted-foreground)',
  rose: 'var(--color-rose)',
  amber: 'var(--color-amber)',
  emerald: 'var(--color-emerald)',
}

/**
 * IconBadge — filled-circle / two-ring-outline icon atom.
 *
 * Source: UntitledUI `Featured icon` (master `1102:5338`, `Type=Light`) and
 * `Featured icon outline` (master `4843:410985`) — see
 * `.claude/skills/design-system/untitledui-reference.md` §3. Neither master set
 * carries a state axis (confirmed by enumerating all 88 variant names). The
 * master's own ladder starts at `sm` (32/16); `xs` (24/12) is an addition
 * below that floor — see the comment on `CONTAINER_SIZE` for why.
 *
 * WHEN: a small circular icon accent next to a title/description — error states,
 * insight callouts, a section's leading glyph. `error-card.tsx` is the reference
 * consumer (`md` / `rose` / `filled`).
 * NEVER for `EmptyState`'s icon — its master frames the icon in a bordered
 * square, a different icon family from a different size axis. Do not "unify"
 * the two atoms.
 * NEVER pass a `className` size/color override at the call site — use the
 * `tone`/`theme`/`size` props so every instance stays traceable to the matrix
 * above (mirrors `TrendDelta`'s no-per-call-override rule, DES-02's sibling for
 * this atom).
 *
 * Deliberately NOT implemented — the master's other three `Type` values:
 *   - `Dark` / `Modern` — carry the skeuomorphic bevel this codebase flattens
 *     everywhere (DES-06).
 *   - `Gradient` — a baked gradient image; neon/gradient treatments are
 *     marketing-only (DES-10).
 *   - `Modern neue` — a two-shell neumorphic stack with ad hoc, un-tokenised
 *     shadows.
 * `xl` (56/28) is also dropped — no dashboard call site needs it.
 *
 * `theme="outline"` draws exactly two rings (never three, per the master): same
 * hue, inner ring 30% opacity, outer ring 10%, both a constant 2px stroke that
 * does NOT scale with `size`, offset a fixed +4px / +9px per side at every size.
 * That fixed-offset geometry does not live on the 4px spacing scale (9px isn't a
 * multiple of 4), so it is drawn via inline `style` rather than an arbitrary
 * Tailwind bracket class — DES-13 targets off-scale utility classes for layout
 * rhythm; this is a literal, non-reusable master measurement for one atom's
 * intrinsic geometry, not a spacing decision.
 */
export function IconBadge({
  icon,
  tone = 'neutral',
  theme = 'filled',
  size = 'md',
  className,
}: IconBadgeProps): React.ReactElement {
  const ringVar = OUTLINE_RING_VAR[tone]

  return (
    <span
      data-slot="icon-badge"
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-full',
        CONTAINER_SIZE[size],
        GLYPH_SIZE[size],
        theme === 'filled' ? FILLED_CLASS[tone] : OUTLINE_TEXT_CLASS[tone],
        className
      )}
    >
      {icon}
      {theme === 'outline' && (
        <>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute rounded-full"
            style={{
              inset: '-4px',
              border: `2px solid color-mix(in oklch, ${ringVar} 30%, transparent)`,
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute rounded-full"
            style={{
              inset: '-9px',
              border: `2px solid color-mix(in oklch, ${ringVar} 10%, transparent)`,
            }}
          />
        </>
      )}
    </span>
  )
}
